import {
  AlertTypes,
  apiClient,
  setAlert,
  MuiIcons,
  GridSortModel,
  GridFilterModel,
} from '@leap4/frontend';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { reportingBaseUrl } from '../../../config/generals';
import { useAppDispatch } from '../../../store/hooks';
import { urlService } from '../../routing/urlsService';
import ReportHeader from '../common/ReportHeader/ReportHeader';
import ReportTabs from './base/reportTabs';
import { useAsyncQuery } from './hooks/useAsyncQuery';
import { initialReportObj, IReport, IReportingResult, IValidationErrors } from './report.interface';
import { CursorCache, IPaginationModel, ReportContext } from './reportContext';
import {
  gridFilterModelToFilters,
  quickSearchFromModel,
  sortModelToParam,
  type IGridQueryExtras,
} from './grid/gridQuery.adapters';
import {
  cloneReportConfig,
  getReportQueryFingerprint,
  pollQueryUntilDone,
} from './report.helpers';
import {
  ADVERTISER_EVENT_METRICS,
  eventColumnKeyFromEventName,
  eventNameFromMatchedEventMetricKey,
  eventNameFromMetricKey,
  eventNameFromSumEventMetricKey,
  formatTimestampUtc,
  getTimeFromAggregationPresets,
  isEventMetricKey,
  eventNameFromPredictedMetricKey,
  isPredictedMetricKey,
  PREDICTION_ANCHOR_METRIC,
  predictedColumnKeyFromEventName,
  predictedErrorColumnKeyFromEventName,
  isMatchedEventMetricKey,
  isSumEventMetricKey,
  matchedEventColumnKeyFromEventName,
  sumEventColumnKeyFromEventName,
  rawPixelOnlyRangeError,
  type IColumnFormatSpec,
} from './reports';
import { useReportingMetadata } from './hooks/useReportingMetadata';
import { buildReportColumns, metricColumnName } from './grid/reportColumns';

const { Assessment: AssessmentIcon } = MuiIcons;

const isSameSortModel = (a: GridSortModel, b: GridSortModel): boolean =>
  a.length === b.length &&
  a.every((item, i) => item.field === b[i].field && item.sort === b[i].sort);

const ReportingModule = () => {

  const metadata = useReportingMetadata();

  // Read through a ref: buildQueryBody and validateFields are not memoised and the
  // callbacks that use them do not list them as deps, so a stale copy would route base
  // metrics to the formula evaluator.
  const metadataRef = useRef(metadata);
  useEffect(() => {
    metadataRef.current = metadata;
  }, [metadata]);
  const dispatch = useAppDispatch();
  const [report, setReport] = useState<IReport>(initialReportObj);
  const [showReportSettings, setShowReportSettings] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [reportTemplates] = useState<IReport[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<IValidationErrors>({});
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<IPaginationModel>({
    page: 0,
    pageSize: 100,
    cursor: null,
    nextCursor: null,
    hasMore: false,
  });
  const paginationRef = useRef<IPaginationModel>({
    page: 0,
    pageSize: 100,
    cursor: null,
    nextCursor: null,
    hasMore: false,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingLeadIds, setIsExportingLeadIds] = useState(false);
  const cursorCacheRef = useRef<CursorCache>(new Map([[0, null]]));
  const lastRunQueryFingerprintRef = useRef<string | null>(null);
  const lastRunReportRef = useRef<IReport | null>(null);
  const [reportData, setReportData] = useState<IReportingResult[] | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);

  const [reportTotals, setReportTotals] = useState<IReportingResult | null>(null);
  const reportTotalsRef = useRef<IReportingResult | null>(null);
  const updateReportTotals = useCallback((value: IReportingResult | null) => {
    reportTotalsRef.current = value;
    setReportTotals(value);
  }, []);

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({ items: [] });
  const sortModelRef = useRef<GridSortModel>([]);
  const gridFilterModelRef = useRef<GridFilterModel>({ items: [] });
  const gridChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reportQuery = useAsyncQuery<{
    data: IReportingResult[];
    totalCount: number | null;
    nextCursor?: string | null;
    hasMore?: boolean;
    page?: number;
    pageSize?: number;
    totals?: IReportingResult | null;
    series?: IReportingResult[];
  }>();

  /**
   * The chart's own read: the same report grouped by date alone, unpaginated. The grid is one page of
   * a cohort-split result, and with five dimensions a page is often a single day — charting it drew a
   * line one point long. Cheap despite being a second query: no cohort dimensions means it answers
   * from the coarse daily rollup.
   */
  const chartQuery = useAsyncQuery<{ data: IReportingResult[] }>();
  const [chartRows, setChartRows] = useState<IReportingResult[] | null>(null);
  /** Held until the grid answers: it says whether the chart still needs a scan of its own. */
  const pendingChartReportRef = useRef<IReport | null>(null);

  const validateFields = (reportToValidate: IReport, isForSavingTemplates: boolean): boolean => {
    const errors: IValidationErrors = {};

    if (!isForSavingTemplates && !metadataRef.current.loaded) {
      errors.metrics = 'Still loading the metric catalogue — try again in a moment';
    }
    if (!reportToValidate.name && isForSavingTemplates) errors.name = 'Template Name is required';
    if (!reportToValidate.createdBy && isForSavingTemplates)
      errors.createdBy = 'Created By is required';
    if (!reportToValidate.description && isForSavingTemplates)
      errors.description = 'Description is required';
    if (!reportToValidate.idCampaign) errors.idCampaign = 'Please select a campaign';
    if (!reportToValidate.startDate || Number.isNaN(new Date(reportToValidate.startDate).getTime()))
      errors.startDate = 'Start Date is required';
    if (!reportToValidate.endDate || Number.isNaN(new Date(reportToValidate.endDate).getTime()))
      errors.endDate = 'End Date is required';
    if (reportToValidate.dimensions.length === 0)
      errors.dimensions = 'At least one dimension is required';
    if (reportToValidate.metrics.length === 0) errors.metrics = 'At least one metric is required';

    // The builder disables Run on this, but a saved template or a restored report can still arrive
    // out of range, so the same check gates the submit.
    if (!errors.startDate && !errors.endDate) {
      const rangeError = rawPixelOnlyRangeError(
        reportToValidate.metrics,
        reportToValidate.startDate,
        reportToValidate.endDate,
      );
      if (rangeError) errors.startDate = rangeError;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildQueryBody = (
    reportData: IReport,
    pagination: IPaginationModel,
    useCursor: boolean = true,
    extras?: IGridQueryExtras
  ): Record<string, any> => {
    const selectedEventMetrics = reportData.metrics
      .map((m) => m.eventType)
      .filter(isEventMetricKey)
      .map(eventNameFromMetricKey)
      .filter(Boolean);
    const selectedMatchedEventMetrics = reportData.metrics
      .map((m) => m.eventType)
      .filter(isMatchedEventMetricKey)
      .map(eventNameFromMatchedEventMetricKey)
      .filter(Boolean);
    const selectedSumEventMetrics = reportData.metrics
      .map((m) => m.eventType)
      .filter(isSumEventMetricKey)
      .map(eventNameFromSumEventMetricKey)
      .filter(Boolean);
    const attributedEvents = Array.from(new Set(selectedEventMetrics));
    const matchedEvents = Array.from(new Set(selectedMatchedEventMetrics));
    const sumEvents = Array.from(new Set(selectedSumEventMetrics));

    const selectedCoreMetrics = reportData.metrics
      .map((m) => m.eventType)
      .filter(
        (metric) =>
          !isEventMetricKey(metric) &&
          !isMatchedEventMetricKey(metric) &&
          !isSumEventMetricKey(metric) &&
          !isPredictedMetricKey(metric)
      );

    // Predicted metrics are served by a separate read rather than as query columns, so they leave
    // outputMetrics and become their own request field.
    const predictedEvents = Array.from(
      new Set(
        reportData.metrics
          .map((m) => m.eventType)
          .filter(isPredictedMetricKey)
          .map(eventNameFromPredictedMetricKey)
          .filter(Boolean)
      )
    );

    const baseMetricKeys = new Set(metadataRef.current.metrics.map((m) => m.key));
    const coreMetrics = selectedCoreMetrics.filter((m) => baseMetricKeys.has(m));
    const calculatedMetricKeys = selectedCoreMetrics.filter((m) => !baseMetricKeys.has(m));

    const hasAdvertiser =
      reportData.reportType === 'advertiser' ||
      coreMetrics.some((m) => ADVERTISER_EVENT_METRICS.has(m));
    const dateDimKey = hasAdvertiser ? 'event_date' : 'pixel_date';

    // Only the advertiser family still folds the foreign date onto its own axis.
    const userSelectedDimensions = reportData.dimensions.map((d) =>
      hasAdvertiser && d.key === 'pixel_date' ? 'event_date' : d.key
    );

    const shouldAutoAddDate =
      reportData.timeAggregation &&
      reportData.timeAggregation !== 'Sum' &&
      !userSelectedDimensions.includes(dateDimKey);

    const finalDimensions = shouldAutoAddDate
      ? [dateDimKey, ...userSelectedDimensions]
      : userSelectedDimensions;

    const outputMetrics = [...coreMetrics];

    const builderFilters = reportData.filters
      .filter((f) => f.dimension && f.specTarget.length > 0)
      .map((f) => ({
        dimension: f.dimension,
        action: f.action,
        values: f.specTarget.map((t) => t.value),
      }));

    const filters =
      extras?.gridFilters && extras.gridFilters.length > 0
        ? [...builderFilters, ...extras.gridFilters]
        : builderFilters;

    // Drop half-filled rows. A blank value would otherwise send NaN, and a constraint on
    // a metric no longer selected is a 400 the user cannot see the cause of. Predicted metrics are
    // excluded for the same reason: they are merged in after the query, so no SQL expression exists
    // to constrain and the backend rejects the whole report.
    const selectedMetricColumns = new Set(
      reportData.metrics
        .filter((m) => !isPredictedMetricKey(m.eventType))
        .map((m) => metricColumnName(m.eventType))
    );
    const constraints = (reportData.constraints || [])
      .filter(
        (c) =>
          c.metric &&
          c.value !== '' &&
          Number.isFinite(Number(c.value)) &&
          selectedMetricColumns.has(c.metric)
      )
      .map((c) => ({
        metric: c.metric,
        operator: c.operator,
        value: Number(c.value),
      }));

    // Anchored here rather than left to the backend's "no metrics at all" default, which fires for a
    // report of nothing but predictions and so put a column on screen nobody asked for and nobody
    // could remove. Explicit means it can be seen, and once anything else is selected it goes away.
    if (
      outputMetrics.length === 0 &&
      attributedEvents.length === 0 &&
      matchedEvents.length === 0 &&
      sumEvents.length === 0 &&
      calculatedMetricKeys.length === 0 &&
      predictedEvents.length > 0
    ) {
      outputMetrics.push(PREDICTION_ANCHOR_METRIC);
    }

    const userSelectedDate = reportData.dimensions.some(
      (d) => d.key === 'pixel_date' || d.key === 'event_date'
    );
    let timeAggregation = reportData.timeAggregation;
    if (timeAggregation === 'Sum' && userSelectedDate) {
      timeAggregation = 'Day';
    } else if (timeAggregation === 'Sum') {
      timeAggregation = undefined;
    }

    const body: Record<string, any> = {
      startDate: reportData.startDate,
      endDate: reportData.endDate,
      dimensions: finalDimensions,
      outputMetrics,
      attributedEvents,
      matchedEvents,
      sumEvents,
      predictedEvents,
      calculatedMetrics: calculatedMetricKeys,
      filters,
      constraints,
      pageSize: pagination.pageSize,
      idCampaign:
        reportData.idCampaign && reportData.idCampaign !== 'all'
          ? reportData.idCampaign
          : undefined,
      timezone: reportData.timezone || undefined,
      metricFamily: hasAdvertiser ? 'advertiser' : 'pixel',
    };

    if (timeAggregation) body.timeAggregation = timeAggregation;

    if (extras?.sort) body.sort = extras.sort;
    if (extras?.search) body.search = extras.search;

    if (useCursor && pagination.cursor) {
      body.cursor = pagination.cursor;
    } else {
      body.page = pagination.page;
    }

    if (reportTotalsRef.current !== null) {
      body.includeTotals = false;
    }

    return body;
  };

  const getReportColumns = useCallback(
    (data: any, reportConfig: IReport, calcFormats?: Record<string, IColumnFormatSpec>): any[] =>
      buildReportColumns(data, reportConfig, metadata, calcFormats),
    [metadata]
  );

  const processReportResult = useCallback(
    (
      result: {
        data: IReportingResult[];
        totalCount: number | null;
        nextCursor?: string | null;
        hasMore?: boolean;
        calculatedMetricFormats?: Record<string, IColumnFormatSpec>;
        totals?: IReportingResult | null;
      },
      reportConfig: IReport
    ) => {
      const newTotalCount = result.totalCount;
      if (typeof newTotalCount === 'number' && newTotalCount >= 0) {
        setTotalCount(newTotalCount);
      }

      if (result.totals !== undefined && reportTotalsRef.current === null) {
        updateReportTotals(result.totals ?? null);
      }

      const nextCursor = result.nextCursor || null;
      const hasMore = result.hasMore ?? false;

      if (nextCursor) {
        cursorCacheRef.current.set(paginationRef.current.page + 1, nextCursor);
      }

      paginationRef.current = { ...paginationRef.current, nextCursor, hasMore };
      setPaginationModel((prev) => ({ ...prev, nextCursor, hasMore }));

      if (!result.data || result.data.length === 0) {
        setReportData([]);
        return;
      }

      const formattedData = result.data.map((row) => {
        const formattedRow: any = { ...row };
        if (formattedRow.pixel_date) {
          formattedRow.pixel_date = dayjs(formattedRow.pixel_date).format('YYYY-MM-DD');
        }
        if (formattedRow.event_date) {
          formattedRow.event_date = dayjs(formattedRow.event_date).format('YYYY-MM-DD');
        }
        delete formattedRow.total_impression_count;

        return formattedRow;
      });

      setColumns(getReportColumns(formattedData[0], reportConfig, result.calculatedMetricFormats));
      setReportData(formattedData);
    },
    [getReportColumns, updateReportTotals]
  );

  /**
   * Same report, grouped by date alone. Constraints are left off deliberately: they are comparisons
   * against the grid's grouping, and a per-date row is not that row, so carrying them over would drop
   * dates the grid still counts. Predictions are left off because they arrive from their own endpoint.
   */
  const submitChartQuery = useCallback(
    (formattedReport: IReport) => {
      setChartRows(null);
      const body = buildQueryBody(formattedReport, { page: 0, pageSize: 0, cursor: null }, false);
      const dateKey = (body.dimensions || []).find(
        (d: string) => d === 'pixel_date' || d === 'event_date'
      );
      // Without a date dimension the report's own metrics have nothing to be drawn against, and the
      // chart falls back to the predictions, which carry their own dates.
      if (!dateKey) return;

      delete body.page;
      delete body.pageSize;
      delete body.cursor;
      delete body.constraints;
      delete body.predictedEvents;
      body.dimensions = [dateKey];
      body.includeTotals = false;
      chartQuery.submit(body);
    },
    [chartQuery]
  );

  const toChartRows = useCallback(
    (rows: IReportingResult[]): IReportingResult[] =>
      (rows ?? []).map((row: any) => {
        const formatted = { ...row };
        if (formatted.pixel_date) {
          formatted.pixel_date = dayjs(formatted.pixel_date).format('YYYY-MM-DD');
        }
        if (formatted.event_date) {
          formatted.event_date = dayjs(formatted.event_date).format('YYYY-MM-DD');
        }
        return formatted;
      }),
    []
  );

  useEffect(() => {
    if (!chartQuery.isCompleted || !chartQuery.data) return;
    setChartRows(toChartRows(chartQuery.data.data ?? []));
  }, [chartQuery.isCompleted, chartQuery.data, toChartRows]);

  useEffect(() => {
    if (reportQuery.status === 'running') {
      setReportError(null);
    }
  }, [reportQuery.status]);

  // useAsyncQuery hands back a new object every render, so submitChartQuery is a new function
  // every render too. Listing it as a dependency re-runs the effect on every render.
  const submitChartQueryRef = useRef(submitChartQuery);
  submitChartQueryRef.current = submitChartQuery;

  useEffect(() => {
    if (reportQuery.isCompleted && reportQuery.data) {
      const activeReport = lastRunReportRef.current || report;
      processReportResult(reportQuery.data, activeReport);

      // The statement rolled the whole report up by date as well, so the chart's own scan of the
      // same rows is not issued at all. Absent, it runs now rather than alongside — two raw-pixel
      // scans at once hold two pool connections for one Run.
      // Only on the completion of a Run that asked for a chart: a pagination refetch lands here
      // too, and setting the rows again would be a fresh array on every pass.
      const pending = pendingChartReportRef.current;
      pendingChartReportRef.current = null;
      if (pending) {
        if (reportQuery.data.series) {
          setChartRows(toChartRows(reportQuery.data.series));
        } else {
          submitChartQueryRef.current(pending);
        }
      }
    }
    if (reportQuery.status === 'failed' && reportQuery.error) {
      pendingChartReportRef.current = null;
      const message = reportQuery.error.message || 'Could not fetch reporting data';
      dispatch(setAlert({
        msg: message,
        type: AlertTypes.FAILURE,
        d: dispatch,
      }));

      setReportError(message);
      setReportData([]);
    }
  }, [
    reportQuery.isCompleted,
    reportQuery.data,
    reportQuery.status,
    reportQuery.error,
    processReportResult,
    dispatch,
    report,
    toChartRows,
  ]);

  const formatReportDates = (reportToFormat: IReport): IReport => {
    let formattedReport = { ...reportToFormat };

    if (formattedReport.isRelativePeriod) {
      const { startDate, endDate } = getTimeFromAggregationPresets(formattedReport.preset);
      if (startDate && endDate) {
        formattedReport = { ...formattedReport, startDate, endDate };
      }
    }

    return {
      ...formattedReport,
      startDate: formattedReport.startDate
        ? dayjs(formattedReport.startDate).format('YYYY-MM-DD')
        : null,
      endDate: formattedReport.endDate ? dayjs(formattedReport.endDate).format('YYYY-MM-DD') : null,
    };
  };

  const saveReportTemplate = async (): Promise<boolean> => {
    if (!report.name) {
      setReport({ ...report, isRelativePeriod: true });
    }

    if (!validateFields(report, true)) {
      setShowReportSettings(true);
      return false;
    }

    console.log('Saving report template:', report);
    dispatch(setAlert({
      msg: 'Report template saving is not yet implemented',
      type: AlertTypes.WARNING,
      d: dispatch,
    }));

    setTabIndex(1);
    setShowReportSettings(false);
    setReport(initialReportObj);
    return true;
  };

  const resetGridSortAndFilter = () => {
    if (gridChangeTimerRef.current) {
      clearTimeout(gridChangeTimerRef.current);
      gridChangeTimerRef.current = null;
    }
    sortModelRef.current = [];
    gridFilterModelRef.current = { items: [] };
    setSortModel([]);
    setGridFilterModel({ items: [] });
  };

  const runReportTemplate = async (templateReport: IReport) => {
    let formattedReport = {
      ...templateReport,
      createdBy: '',
      description: '',
      name: '',
      _id: null,
    };
    formattedReport = formatReportDates(formattedReport);
    setReport(formattedReport);
    setTabIndex(0);
    resetGridSortAndFilter();
    cursorCacheRef.current = new Map([[0, null]]);
    paginationRef.current = {
      page: 0,
      pageSize: paginationRef.current.pageSize,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    };
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    }));

    lastRunReportRef.current = cloneReportConfig(formattedReport);
    lastRunQueryFingerprintRef.current = getReportQueryFingerprint(formattedReport, buildQueryBody);
    const body = buildQueryBody(formattedReport, paginationRef.current);
    reportQuery.submit(body);
    pendingChartReportRef.current = formattedReport;
    setChartRows(null);
  };

  const deleteReport = async (templateReport: IReport) => {
    const isConfirmed = globalThis.confirm(
      'Are you sure you want to delete this report? This action cannot be undone.'
    );
    if (isConfirmed) {
      console.log('Deleting report:', templateReport);
      dispatch(setAlert({
        msg: 'Report template deletion is not yet implemented',
        type: AlertTypes.WARNING,
        d: dispatch,
      }));
    }
  };

  const updateReport = async (templateReport: IReport) => {
    const formattedReport = formatReportDates(templateReport);
    setTabIndex(0);
    setReport(formattedReport);
  };

  const clearReport = () => {
    setReport(initialReportObj);
    setShowReportSettings(false);
    setValidationErrors({});
    resetGridSortAndFilter();
    cursorCacheRef.current = new Map([[0, null]]);
    setPaginationModel({ page: 0, pageSize: 100, cursor: null, nextCursor: null, hasMore: false });
    paginationRef.current = {
      page: 0,
      pageSize: 100,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    };
    setTotalCount(0);
    setReportData(null);
    updateReportTotals(null);
    lastRunQueryFingerprintRef.current = null;
    lastRunReportRef.current = null;
    reportQuery.reset();
  };

  const exportLeadIds = useCallback(async () => {
    let startDate = report.startDate;
    let endDate = report.endDate;

    if (report.isRelativePeriod && report.preset !== 'choose time preset') {
      const relativeDates = getTimeFromAggregationPresets(report.preset);
      startDate = relativeDates.startDate;
      endDate = relativeDates.endDate;
    }

    if (!startDate || !endDate) {
      dispatch(setAlert({
        msg: 'Please select Start Date and End Date',
        type: AlertTypes.WARNING,
        d: dispatch,
      }));
      return;
    }

    const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
    const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');

    setIsExportingLeadIds(true);
    try {
      const body: Record<string, any> = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      };
      if (report.idCampaign && report.idCampaign !== 'all') {
        body.idCampaign = report.idCampaign;
      }

      const submitRes = await apiClient.post<{ queryId: string }>(
        `${reportingBaseUrl}/queries/lead-ids`,
        body
      );
      const result = await pollQueryUntilDone<{
        leadIds: string[];
        leadIdsWithTimestamps?: Array<{ leadId: string; impressionTimestamp: string | null }>;
        totalCount: number;
      }>(submitRes.data.queryId);

      if (result.status === 'failed') {
        throw new Error(result.error?.message || 'Lead IDs query failed');
      }

      const leadRows =
        result.result?.leadIdsWithTimestamps ||
        result.result?.leadIds?.map((leadId) => ({ leadId, impressionTimestamp: null }));
      if (!leadRows || leadRows.length === 0) {
        dispatch(setAlert({
          msg: 'No lead IDs found for the selected date range',
          type: AlertTypes.WARNING,
          d: dispatch,
        }));
        return;
      }

      const escapeCsvValue = (value: string | null): string => {
        const stringValue = value ?? '';
        if (
          stringValue.includes(',') ||
          stringValue.includes('"') ||
          stringValue.includes('\n') ||
          stringValue.includes('\r')
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };
      const formatExportTimestamp = (value: string | null): string =>
        value ? formatTimestampUtc(value) : '';
      const csvRows = leadRows.map((row) =>
        [
          escapeCsvValue(row.leadId),
          escapeCsvValue(formatExportTimestamp(row.impressionTimestamp)),
        ].join(',')
      );
      const csvContent = ['lead_id,impression_timestamp', ...csvRows].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lead_ids_${formattedStartDate}_to_${formattedEndDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      dispatch(setAlert({
        msg: `Exported ${leadRows.length} lead IDs`,
        type: AlertTypes.SUCCESS,
        d: dispatch,
      }));
    } catch (err: any) {
      console.error('Failed to export lead IDs:', err);
      dispatch(setAlert({
        msg: err.message || 'Could not export lead IDs',
        type: AlertTypes.FAILURE,
        d: dispatch,
      }));
    } finally {
      setIsExportingLeadIds(false);
    }
  }, [
    report.startDate,
    report.endDate,
    report.isRelativePeriod,
    report.preset,
    report.idCampaign,
    dispatch,
  ]);

  const exportAllAsCsv = useCallback(async () => {
    if (!validateFields(report, false)) {
      return;
    }

    const formattedReport = formatReportDates(report);
    const exportFingerprint = getReportQueryFingerprint(formattedReport, buildQueryBody);
    if (
      lastRunQueryFingerprintRef.current &&
      exportFingerprint !== lastRunQueryFingerprintRef.current
    ) {
      const proceed = globalThis.confirm(
        'Report settings have changed since the last Run Report. The grid may not match this export. Continue anyway?'
      );
      if (!proceed) return;
    }

    setIsExporting(true);
    try {

      const body = buildQueryBody(
        formattedReport,
        { page: 0, pageSize: 0, cursor: null },
        false
      );
      delete body.page;
      delete body.pageSize;
      delete body.cursor;

      const dimensionKeys: string[] = body.dimensions || [];
      const columnKeys = [
        ...dimensionKeys,
        ...(body.outputMetrics || []),
        ...(body.attributedEvents || []).map(eventColumnKeyFromEventName),
        ...(body.matchedEvents || []).map(matchedEventColumnKeyFromEventName),
        ...(body.sumEvents || []).map(sumEventColumnKeyFromEventName),
        // Both columns, as the grid produces: the export listed only the total, so the margin of error
        // was the one figure on screen that could not leave with it.
        ...(body.predictedEvents || []).flatMap((event: string) => [
          predictedColumnKeyFromEventName(event),
          predictedErrorColumnKeyFromEventName(event),
        ]),
        ...(body.calculatedMetrics || []),
      ];
      const dimensionKeySet = new Set(dimensionKeys);
      const sampleRow: Record<string, any> = {};
      for (const key of columnKeys) sampleRow[key] = dimensionKeySet.has(key) ? '' : 0;

      const resolvedColumns = getReportColumns(sampleRow, formattedReport);
      if (resolvedColumns.length === 0) {
        dispatch(setAlert({ msg: 'No columns to export', type: AlertTypes.WARNING, d: dispatch }));
        return;
      }
      const fileName = `conversion_report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
      body.columns = resolvedColumns.map((col) => ({ key: col.name, label: col.label }));
      body.fileName = fileName;

      const submitRes = await apiClient.post<{ queryId: string }>(
        `${reportingBaseUrl}/queries/export`,
        body
      );
      const result = await pollQueryUntilDone<{
        downloadReady: boolean;
        rowCount: number;
        fileName: string;
        byteSize: number;
      }>(submitRes.data.queryId);

      if (result.status === 'failed') {
        throw new Error(result.error?.message || 'Export query failed');
      }

      const exportResult = result.result;
      if (!exportResult || !exportResult.downloadReady || exportResult.rowCount === 0) {
        dispatch(setAlert({ msg: 'No data to export', type: AlertTypes.WARNING, d: dispatch }));
        return;
      }

      const fileRes = await apiClient.get(
        `${reportingBaseUrl}/queries/${submitRes.data.queryId}/download`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(fileRes.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportResult.fileName || fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      dispatch(setAlert({
        msg: `Exported ${exportResult.rowCount} rows`,
        type: AlertTypes.SUCCESS,
        d: dispatch,
      }));
    } catch (err: any) {
      console.error('Failed to export report:', err);
      dispatch(setAlert({
        msg: err.message || 'Could not export report data',
        type: AlertTypes.FAILURE,
        d: dispatch,
      }));
    } finally {
      setIsExporting(false);
    }
  }, [report, dispatch, getReportColumns]);

  const buildGridExtras = useCallback(
    (): IGridQueryExtras => ({
      sort: sortModelToParam(sortModelRef.current),
      search: quickSearchFromModel(gridFilterModelRef.current),
      gridFilters: gridFilterModelToFilters(gridFilterModelRef.current),
    }),
    []
  );

  const resetToFirstPage = useCallback(() => {
    cursorCacheRef.current = new Map([[0, null]]);
    paginationRef.current = {
      page: 0,
      pageSize: paginationRef.current.pageSize,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    };
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    }));
  }, []);

  const onPaginationModelChange = useCallback(
    (model: IPaginationModel) => {
      // The grid fires this when Run Report resets it to page 0, not only when the user
      // pages. Submitting on a page that did not actually change duplicates the run the
      // caller is already making.
      const pageUnchanged =
        model.page === paginationRef.current.page &&
        model.pageSize === paginationRef.current.pageSize;

      const cachedCursor = cursorCacheRef.current.get(model.page);
      const activeReport = lastRunReportRef.current || report;

      const newModel: IPaginationModel = {
        ...model,
        cursor: cachedCursor ?? null,
        nextCursor: paginationRef.current.nextCursor,
        hasMore: paginationRef.current.hasMore,
      };

      setPaginationModel(newModel);
      paginationRef.current = newModel;

      if (pageUnchanged) return;

      const body = buildQueryBody(activeReport, newModel, true, buildGridExtras());
      reportQuery.submit(body);
    },
    [report, reportQuery, buildGridExtras]
  );

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      // Same as pagination: Run Report clears the sort, which makes the grid fire this
      // with a model identical to the one already held.
      const sortUnchanged = isSameSortModel(model, sortModelRef.current);

      setSortModel(model);
      sortModelRef.current = model;
      if (sortUnchanged || !lastRunReportRef.current) return;
      resetToFirstPage();
      const body = buildQueryBody(
        lastRunReportRef.current,
        paginationRef.current,
        false,
        buildGridExtras()
      );
      reportQuery.submit(body);
    },
    [reportQuery, resetToFirstPage, buildGridExtras]
  );

  const submitGridFilterChange = useCallback(() => {
    if (!lastRunReportRef.current) return;
    resetToFirstPage();

    updateReportTotals(null);
    const body = buildQueryBody(
      lastRunReportRef.current,
      paginationRef.current,
      false,
      buildGridExtras()
    );
    reportQuery.submit(body);
  }, [reportQuery, resetToFirstPage, buildGridExtras, updateReportTotals]);

  const onFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      // Run Report clears the filter model, and the grid echoes that back here. Without
      // this guard the echo schedules a debounced submit that lands 400ms after the run
      // the caller already started.
      const filterUnchanged =
        JSON.stringify(model) === JSON.stringify(gridFilterModelRef.current);

      setGridFilterModel(model);
      gridFilterModelRef.current = model;
      if (filterUnchanged) return;
      if (gridChangeTimerRef.current) clearTimeout(gridChangeTimerRef.current);
      gridChangeTimerRef.current = setTimeout(() => submitGridFilterChange(), 400);
    },
    [submitGridFilterChange]
  );

  const runReportFromBuilder = useCallback(() => {
    if (!validateFields(report, false)) return;

    resetGridSortAndFilter();
    cursorCacheRef.current = new Map([[0, null]]);
    paginationRef.current = {
      page: 0,
      pageSize: paginationRef.current.pageSize,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    };
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    }));
    setReportData(null);
    updateReportTotals(null);

    const formattedReport = formatReportDates(report);
    lastRunReportRef.current = cloneReportConfig(formattedReport);
    lastRunQueryFingerprintRef.current = getReportQueryFingerprint(formattedReport, buildQueryBody);
    const body = buildQueryBody(formattedReport, paginationRef.current);
    reportQuery.submit(body);
    pendingChartReportRef.current = formattedReport;
    setChartRows(null);
  }, [report, reportQuery, submitChartQuery, updateReportTotals]);

  const cancelRunningQuery = useCallback(() => {
    // Only announce a cancel the user asked for — a superseded query is also
    // terminated, and saying so would report something they did not do.
    if (!reportQuery.isRunning) return;

    // A cancelled Run must not go on to submit the chart's own job for a report nobody wants.
    pendingChartReportRef.current = null;
    void reportQuery.cancel().then(() => {
      dispatch(
        setAlert({ msg: 'Report cancelled', type: AlertTypes.WARNING, d: dispatch }),
      );
    });
  }, [reportQuery, dispatch]);

  const isFetchingReportData = reportQuery.isRunning;

  const reportContextValue = useMemo(
    () => ({
      report,
      reportTemplates,
      setReport,
      showReportSettings,
      tabIndex,
      setTabIndex,
      columns,
      reportData,
      chartRows,
      runReport: runReportFromBuilder,
      isFetchingReportData,
      reportError,
      metadata,
      validationErrors,
      totalCount,
      totals: reportTotals,
      paginationModel,
      onPaginationModelChange,
      sortModel,
      onSortModelChange,
      filterModel: gridFilterModel,
      onFilterModelChange,
      exportAllAsCsv,
      exportLeadIds,
      isExporting,
      isExportingLeadIds,
      queryStatus: reportQuery.status,
      queryElapsedMs: reportQuery.elapsedMs,
      cancelQuery: cancelRunningQuery,
    }),
    [
      report,
      showReportSettings,
      reportTemplates,
      tabIndex,
      columns,
      reportData,
      isFetchingReportData,
      reportError,
      metadata,
      validationErrors,
      runReportFromBuilder,
      totalCount,
      reportTotals,
      paginationModel,
      onPaginationModelChange,
      sortModel,
      onSortModelChange,
      gridFilterModel,
      onFilterModelChange,
      exportAllAsCsv,
      exportLeadIds,
      isExporting,
      isExportingLeadIds,
      reportQuery.status,
      reportQuery.elapsedMs,
      cancelRunningQuery,
    ]
  );

  return (
    <>
      <ReportHeader
        hideTimeNavbar={true}
        breadcrumbs={[
          {
            link: urlService.reporting(),
            text: 'Conversion Reporting',
            icon: <AssessmentIcon />,
          },
        ]}
      />
      <ReportContext.Provider value={reportContextValue}>
        <ReportTabs
          saveReportTemplate={saveReportTemplate}
          deleteReport={deleteReport}
          runReportTemplate={runReportTemplate}
          clearReport={clearReport}
          updateReport={updateReport}
        />
      </ReportContext.Provider>
    </>
  );
};

export default ReportingModule;
